import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Calendar, User, Tag as TagIcon } from "lucide-react";
import { api } from "../lib/api";
import Comments from "../components/Comments";
import { setPageMeta, SITE_ORIGIN } from "../lib/seo";

export default function BlogPost() {
    const { slug } = useParams();
    const { t, i18n } = useTranslation();
    const [post, setPost] = useState(null);
    const [related, setRelated] = useState([]);
    const [notFound, setNotFound] = useState(false);
    const isHindi = (i18n.resolvedLanguage || i18n.language || "en").startsWith("hi");

    useEffect(() => {
        setPost(null);
        setNotFound(false);
        api.get(`/posts/${slug}`)
            .then((r) => {
                setPost(r.data);
                setPageMeta({
                    title: `${r.data.seo_title || r.data.title} · Femcare.life`,
                    description: r.data.seo_description || r.data.excerpt || "",
                    path: `/blog/${slug}`,
                });
                return api.get(`/posts?category=${r.data.category}&limit=3`);
            })
            .then((r) => {
                if (r) setRelated((r.data || []).filter((p) => p.slug !== slug).slice(0, 2));
            })
            .catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center">
                <h1 className="font-serif text-4xl text-femcare-purple mb-6">Article not found</h1>
                <Link to="/blog" className="btn-primary">Back to Journal</Link>
            </div>
        );
    }

    if (!post) return <div className="py-32 text-center text-femcare-purple/60">Loading...</div>;

    const articleLd = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: post.excerpt,
        image: post.cover_image ? [post.cover_image] : undefined,
        author: { "@type": "Person", name: post.author },
        datePublished: post.created_at,
        dateModified: post.updated_at || post.created_at,
        mainEntityOfPage: `${SITE_ORIGIN}/blog/${post.slug}`,
        publisher: { "@type": "Organization", name: "Femcare.life", logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}/femcare-logo-v2.png` } },
    };

    return (
        <article data-testid="blog-post-page">
            <script type="application/ld+json" data-testid="blog-post-jsonld" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
            <section className="relative overflow-hidden">
                <div className="aurora grain" aria-hidden="true" />
                <div className="max-w-4xl mx-auto px-6 lg:px-12 pt-12 pb-8 relative">
                    <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-femcare-purple/60 hover:text-femcare-pink mb-12" data-testid="back-to-blog">
                        <ArrowLeft className="w-4 h-4" /> Back to Journal
                    </Link>
                    <p className="text-xs uppercase tracking-[0.3em] text-femcare-accent mb-6">
                        {post.category} · {post.read_time} min read
                    </p>
                    <h1 className="editorial-headline text-4xl md:text-5xl lg:text-6xl text-femcare-purple mb-8">
                        {post.title}
                    </h1>
                    <p className="text-xl text-femcare-purple/70 leading-relaxed mb-8 font-light italic">{post.excerpt}</p>
                    <div className="flex items-center gap-6 text-sm text-femcare-purple/60 border-y border-femcare-border py-4">
                        <div className="flex items-center gap-2"><User className="w-4 h-4" /> {post.author}</div>
                        <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /> {new Date(post.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                    </div>
                </div>
            </section>

            <div className="max-w-6xl mx-auto px-6 lg:px-12 mb-12">
                <div className="aspect-[16/9] rounded-3xl overflow-hidden bg-femcare-muted">
                    <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-6 lg:px-12 pb-24">
                <div className="prose-femcare" data-testid="post-content">
                    {post.content.split('\n\n').map((para, i) => {
                        if (para.startsWith('## ')) {
                            return <h2 key={i}>{para.replace('## ', '')}</h2>;
                        }
                        // Render with **bold** markdown
                        const parts = para.split(/(\*\*[^*]+\*\*)/g);
                        return (
                            <p key={i}>
                                {parts.map((part, j) =>
                                    part.startsWith('**') && part.endsWith('**')
                                        ? <strong key={j}>{part.slice(2, -2)}</strong>
                                        : <React.Fragment key={j}>{part}</React.Fragment>
                                )}
                            </p>
                        );
                    })}
                </div>
            </div>

            <Comments slug={slug} />

            {related.length > 0 && (
                <section className="bg-femcare-petal/40 py-20">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12">
                        <h3 className="editorial-headline text-3xl md:text-4xl text-femcare-purple mb-10">
                            Keep reading
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {related.map((p) => (
                                <Link key={p.id} to={`/blog/${p.slug}`} className="group" data-testid={`related-${p.slug}`}>
                                    <div className="aspect-[16/10] rounded-2xl overflow-hidden mb-5 bg-femcare-muted">
                                        <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    </div>
                                    <p className="text-xs uppercase tracking-[0.2em] text-femcare-accent mb-3">{p.category}</p>
                                    <h4 className="font-serif text-2xl text-femcare-purple group-hover:text-femcare-pink transition-colors">{p.title}</h4>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </article>
    );
}
